import { usePatientContext } from '@/components/Contexts/PatientContext';

import SvgChart, { SVGRenderer } from '@wuba/react-native-echarts/svgChart';
import * as echarts from 'echarts/core';
import { useRef, useEffect, useState } from 'react';
import { BarChart, LineChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  MarkAreaComponent,
  LegendComponent,
  MarkLineComponent,
} from 'echarts/components';
import DateUtils from '@/utils/DateUtils';
import { View } from 'react-native';
import {
  GetImplantsQuery,
  SubImplantsSubscription,
  SubPatientSubscription,
  useGetImplantsQuery,
} from '@/data/data';
import visionData from '@/data/visionData';
import { Color } from '@/GlobalStyles';
import SimpleInterpolation from '@/utils/SimpleInterpolation';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next/index';
import { ArrayElement } from '@/utils/typeHelpers';

// Register extensions
echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  SVGRenderer,
  BarChart,
  LineChart,
  MarkAreaComponent,
  LegendComponent,
  MarkLineComponent,
]);

const E_HEIGHT = 400;
const E_WIDTH = 300;

type ImplantsChartProps = {
  decalage?: {
    0?: number;
    1?: number;
  };
  implants: {
    0?: string;
    1?: string;
  };
  decalageCourbePatient?: {
    OG?: number;
    OD?: number;
  };
  courbePatient?: {
    OG?: [number, number][] | null;
    OD?: [number, number][] | null;
  };
  oeilDominant?: 'gauche' | 'droite';
  monovision?: boolean;
};

const decaleImplantData = (data, decalage) => {
  if (data === undefined) {
    return data;
  }
  return data
    .map((item, index) => [item[0] - decalage, item[1]])
    .filter((visionData) => visionData[1] !== null);
};

const calculCombinaison = (data1, data2) => {
  if (data1 === undefined || data2 === undefined) {
    return data1 || data2;
  }
  const finalData1 = data1.filter((item) => item[1] !== null).map((item) => [item[0], item[1]]);
  const finalData2 = data2.filter((item) => item[1] !== null).map((item) => [item[0], item[1]]);
  const spline1 = new SimpleInterpolation(finalData1);
  const spline2 = new SimpleInterpolation(finalData2);

  const xs = [...finalData1.map((d) => d[0]), ...finalData2.map((d) => d[0])]
    .sort((a, b) => b - a)
    .filter((v, i, a) => a.indexOf(v) === i);
  return xs.map((x) => [x, Math.min(spline1.at(x), spline2.at(x))]);
};

const setHabitsData = (patient: SubPatientSubscription['patientByPk']) => {
  const habitudes = patient?.habitudes[0];
  if (!habitudes) {
    return {
      maxPercentPatient: 0,
      dataPatient: undefined,
      intervalsPatient: undefined,
    };
  }
  const minCoucher = DateUtils.getMinutes(DateUtils.getFromTimeSQLFormat(habitudes?.heureCoucher));
  const minReveil = DateUtils.getMinutes(DateUtils.getFromTimeSQLFormat(habitudes?.heureReveil));
  const totalTime =
    minCoucher -
    minReveil -
    DateUtils.getMinutes(DateUtils.getFromTimeSQLFormat(habitudes?.tempsSieste)) +
    (minCoucher < minReveil ? 1440 : 0);

  let maxPercentPatient = 0;

  const dataPatient = [];
  const intervalsPatient = [];

  Object.keys(visionData).map((input) => {
    const visionDatum = visionData[input];
    if (habitudes[input]) {
      const value = DateUtils.getMinutes(DateUtils.getFromTimeSQLFormat(habitudes[input]));
      const percent = parseFloat(totalTime === 0 ? value : ((value / totalTime) * 100).toFixed(2));

      if (percent > maxPercentPatient) {
        maxPercentPatient = percent;
      }

      let item = [parseFloat((-1 / visionDatum.distance).toFixed(2)), percent];
      dataPatient.push(item);
    }
  });

  const activitePreferentielles = patient?.activitePreferentielles;

  activitePreferentielles?.map((value) => {
    intervalsPatient.push([
      {
        name: value.nom,
        xAxis: parseFloat((-1 / value.distanceMin).toFixed(2)),
      },
      {
        xAxis: parseFloat((-1 / value.distanceMax).toFixed(2)),
      },
    ]);
  });

  const filterOccurence = (filtered) => {
    if (!filtered) {
      for (let i = 0; i < dataPatient.length - 1; i++) {
        let indexes = [];

        let occurence = dataPatient.filter((p, index) => {
          if (p[0] === dataPatient[i][0]) {
            indexes.push(index);
            return p;
          }
        });

        if (occurence.length > 1) {
          let value = 0;

          occurence.forEach((point, index) => {
            value = value + point[1];

            if (index > 0) {
              dataPatient.splice(indexes[index], 1);
            }
          });

          dataPatient[i][1] = value;
          filterOccurence(true);
        }
      }
    }
  };

  const sort = (sorted) => {
    let neededSort = false;
    if (!sorted) {
      for (let i = 0; i < dataPatient.length - 1; i++) {
        if (dataPatient[i][0] > dataPatient[i + 1][0]) {
          neededSort = true;

          const temporary = dataPatient[i];

          dataPatient[i] = dataPatient[i + 1];
          dataPatient[i + 1] = temporary;
        }
      }
      sort(!neededSort);
    }
  };
  filterOccurence(false);
  sort(false);

  // filterOccurence cumule les pourcentages d'activités à la même distance,
  // donc le max réel tracé sur la courbe peut être > au max d'une activité
  // isolée. On recalcule ici à partir des points qui seront effectivement
  // affichés pour que l'axe Y englobe correctement le pic.
  const realMaxPercent = dataPatient.reduce(
    (acc, point) => (point[1] > acc ? point[1] : acc),
    0
  );

  const newMaxPatient = parseFloat(
    (realMaxPercent + 5 - (realMaxPercent % 5)).toFixed(2)
  );

  return {
    maxPercentPatient: newMaxPatient,
    dataPatient,
    intervalsPatient,
  };
};

export const getImplantChartOptions = (
  patient: SubPatientSubscription['patientByPk'],
  implant0?: ArrayElement<GetImplantsQuery['implant']>,
  implant1?: ArrayElement<GetImplantsQuery['implant']>,
  decalage: { 0?: number; 1?: number } = { 0: 0, 1: 0 },
  monovision: boolean = false,
  oeilDominant?: 'gauche' | 'droite' = 'gauche',
  width?: number,
  t?: TFunction,
  animation: boolean = true,
  courbePatient: { OG?: [number, number][] | null; OD?: [number, number][] | null } = {
    OG: null,
    OD: null,
  },
  decalageCourbePatient: { OG?: number; OD?: number } = { OG: 0, OD: 0 }
) => {
  const { maxPercentPatient, dataPatient, intervalsPatient } = setHabitsData(patient);

  const isNarrow = width !== undefined && width < 600;

  const option = {
    animation,
    legend: {
      type: 'plain',
      bottom: 0,
      left: 'center',
      width: isNarrow ? '95%' : '90%',
      textStyle: {
        fontSize: isNarrow ? 10 : 12,
      },
      itemGap: isNarrow ? 6 : 10,
      itemWidth: isNarrow ? 16 : 25,
      itemHeight: isNarrow ? 10 : 14,
    },
    tooltip: {
      trigger: 'axis',
      formatter: (value, index) => {
        let hadPatient = false;
        const tooltip = value
          .map((element) => {
            if (element.seriesName === 'Patient' && hadPatient) {
              return null;
            }
            if (element.seriesName === 'Patient') {
              hadPatient = true;
            }
            const unit = element.seriesName !== 'Patient' ? ' AV(LogMAR)' : '%';
            return `${element.seriesName} : ${element.data[0]} δ -> ${element.data[1]}${unit} `;
          })
          .filter((e) => e !== null);

        return `${tooltip.join('<br/>')}`;
      },
      position: [0, 0],
    },
    grid: {
      left: isNarrow ? '5%' : '10%',
      right: isNarrow ? '5%' : '10%',
      bottom: isNarrow ? 80 : 50,
      top: '5%',
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      axisLine: { onZero: false },
      max: 2,
      min: -4,
      inverse: true,
      axisLabel: {
        formatter: (value, index) => {
          return `${value} δ \n ${value < 0 ? `${(-1 / value).toFixed(2)} m` : `${'Infini'}`}`;
        },
        fontSize: isNarrow ? 10 : 12,
        margin: 8,
      },
      interval: isNarrow ? 1 : 0.5,
    },
    yAxis: [
      {
        name: 'Occurence %',
        nameLocation: 'middle',
        nameTextStyle: {
          padding: [0, 0, 17, 0],
        },
        min: 0,
        max: maxPercentPatient > 21 ? maxPercentPatient + 15 : 31.2,
        type: 'value',
        axisLine: { onZero: false },
        splitLine: {
          show: false,
        },
      },
      {
        name: 'AV(LogMAR)',
        nameLocation: 'middle',
        nameTextStyle: {
          padding: [17, 0, 0, 0],
        },
        min: maxPercentPatient > 20 ? ((20 - maxPercentPatient) / 5) * 0.2 : -0.2,
        max: 0.8,
        type: 'value',
        axisLine: { onZero: false },
        inverse: true,
        splitLine: {
          show: false,
        },
        axisLabel: {
          formatter: (value) => {
            if (value >= -0.3) {
              return value;
            } else {
              return '';
            }
          },
        },
      },
    ],
    series: [
      {
        name: t('Habitudes visuelles'),
        id: 'patient',
        type: 'line',
        yAxisIndex: 0,
        smooth: true,
        data: dataPatient,
        symbolSize: 5,
        color: '#91cc75',
        markArea: {
          itemStyle: {
            color: 'rgba(255, 173, 177, 0.4)',
          },
          data: intervalsPatient,
        },
        areaStyle: { origin: 'start', opacity: 0.3 },
      },
      {
        data: null,
        id: 'ligne-vision',
        type: 'line',
        yAxisIndex: 1,
        smooth: true,
        color: Color.MainBleu,
        symbolSize: 5,
        markLine: {
          data: [
            { name: 'Vision', xAxis: -4, yAxis: 0 },
            { xAxis: 2, yAxis: 0 },
            { name: 'Vision', xAxis: -4, yAxis: 0.2 },
            { xAxis: 2, yAxis: 0.2 },
          ],
          dashOffset: 0,
          symbol: ['none', 'none'],
          silent: true,
          label: {
            color: 'transparent',
          },
        },
      },
      {
        name: implant1?.name
          ? (monovision ? t('Oeil droit') : t('Choix 1')) +
            ' : ' +
            implant1?.name +
            (monovision && oeilDominant === 'gauche' ? '\n' + t('Oeil dominé') : '')
          : implant1?.name,
        unit: implant1?.type,
        data: decaleImplantData(implant1?.data, decalage[1] ? decalage[1] : 0),
        id: 'implant',
        type: 'line',
        yAxisIndex: 1,
        smooth: true,
        color: Color.MainBleu,
        symbolSize: 5,
      },
      {
        name: implant0?.name
          ? (monovision ? t('Oeil gauche') : t('Choix 2')) +
            ' : ' +
            implant0?.name +
            (monovision && oeilDominant === 'droit' ? '\n' + t('Oeil dominé') : '')
          : implant0?.name,
        unit: implant0?.type,
        data: decaleImplantData(implant0?.data, decalage[0] ? decalage[0] : 0),
        id: 'implant-2',
        type: 'line',
        yAxisIndex: 1,
        smooth: true,
        color: Color.sECJAUNE,
        symbolSize: 5,
      },
      {
        name: t('Binoculaire'),
        data: calculCombinaison(
          courbePatient.OD
            ? decaleImplantData(
                courbePatient.OD,
                decalageCourbePatient.OD ? decalageCourbePatient.OD : 0
              )
            : decaleImplantData(implant1?.data, decalage[1] ? decalage[1] : 0),
          courbePatient.OG
            ? decaleImplantData(
                courbePatient.OG,
                decalageCourbePatient.OG ? decalageCourbePatient.OG : 0
              )
            : decaleImplantData(implant0?.data, decalage[0] ? decalage[0] : 0)
        ),
        id: 'implant-aire',
        type: 'line',
        yAxisIndex: 1,
        smooth: true,
        color: Color.VertElectrique,
        symbolSize: 0,
        lineStyle: {
          width: 0,
        },
        areaStyle: { origin: 'end', opacity: 0.3 },
      },
    ],
  };

  if (courbePatient.OD) {
    option.series.push({
      name: t('Courbe oeil opéré OD'),
      data: decaleImplantData(
        courbePatient.OD,
        decalageCourbePatient.OD ? decalageCourbePatient.OD : 0
      ),
      id: 'courbe-oeil-patient-OD',
      type: 'line',
      yAxisIndex: 1,
      smooth: true,
      color: '#990F02',
      symbolSize: 5,
    });
  }
  if (courbePatient.OG) {
    option.series.push({
      name: t('Courbe oeil opéré OG'),
      data: decaleImplantData(
        courbePatient.OG,
        decalageCourbePatient.OG ? decalageCourbePatient.OG : 0
      ),
      id: 'courbe-oeil-patient-OG',
      type: 'line',
      yAxisIndex: 1,
      smooth: true,
      color: '#E3242B',
      symbolSize: 5,
    });
  }

  return option;
};

const ImplantsChart = (props: ImplantsChartProps) => {
  const {
    implants = {},
    decalage = { 0: 0, 1: 0 },
    monovision = false,
    oeilDominant = 'gauche',
    courbePatient = { OG: null, OD: null },
    decalageCourbePatient = { OG: 0, OD: 0 },
  } = props;
  const { patient } = usePatientContext();
  const { t } = useTranslation();
  const { data: implantsInfos } = useGetImplantsQuery({
    variables: {
      implantsList: Object.values(implants).filter((implant) => implant !== undefined),
    },
  });

  const implant0 = implantsInfos?.implant.find((implant) => implant.id === implants[0]);
  const implant1 = implantsInfos?.implant.find((implant) => implant.id === implants[1]);

  const chartRef = useRef<any>(null);
  const svgRef = useRef<any>(null);
  const [width, setWidth] = useState(0);

  const dynamicHeight = width > 0 && width < 600 ? E_HEIGHT + 60 : E_HEIGHT;

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.resize({
        width: width,
        height: dynamicHeight,
      });
    }
  }, [width, dynamicHeight]);

  useEffect(() => {
    let chart: any;
    if (svgRef.current && patient) {
      const option = getImplantChartOptions(
        patient,
        implant0,
        implant1,
        decalage,
        monovision,
        oeilDominant,
        width,
        t,
        true,
        courbePatient,
        decalageCourbePatient
      );

      // @ts-ignore
      if (!chartRef.current) {
        chart = echarts.init(svgRef.current, 'light', {
          renderer: 'svg',
          width: width,
          height: dynamicHeight,
        });
        chartRef.current = chart;
      }

      chartRef.current.setOption(option, { replaceMerge: ['series'] });
    }
  }, [
    patient,
    implantsInfos,
    width,
    decalage,
    courbePatient.OG,
    courbePatient.OD,
    decalageCourbePatient.OG,
    decalageCourbePatient.OD,
  ]);

  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
    };
  }, []);

  const handleLayout = (e) => {
    const { width } = e.nativeEvent.layout;
    setWidth(width);
  };

  return (
    <View
      onLayout={handleLayout}
      style={{ flex: 1, flexShrink: 0, flexBasis: 'auto', height: dynamicHeight }}
    >
      <SvgChart ref={svgRef} />
    </View>
  );
};

export default ImplantsChart;
